import { useState, useEffect } from 'react'

const MOBILE_BREAKPOINT = 768

/**
 * Hook to detect if the viewport is mobile-sized.
 * Uses matchMedia for efficient responsive detection.
 *
 * Mobile = width < 768px OR (landscape phone: width < 1024px AND height < 500px).
 * This catches phones rotated to landscape (~812x375) without affecting tablets (768x1024+).
 *
 * @returns boolean | undefined - true if mobile, undefined during SSR/initial render
 */
function detectMobile(): boolean | undefined {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return undefined
    }
    return (
        window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches ||
        window.matchMedia('(max-width: 1023px) and (max-height: 499px)').matches
    )
}

/**
 * TECH-011 — Faut-il neutraliser l'auto-focus (et donc l'ouverture automatique
 * du clavier virtuel) au montage d'une modale / popover / champ ?
 *
 * Vrai sur les appareils tactiles (`pointer: coarse`) OU les viewports
 * « smartphone ». Sur ces contextes, un focus programmatique d'un input fait
 * surgir le clavier logiciel, qui masque la modale qui vient de s'ouvrir — très
 * désagréable. L'utilisateur tape lui-même sur le champ quand il veut saisir.
 *
 * Lecture SYNCHRONE, sans listener : l'auto-focus est un comportement de
 * montage (one-shot), pas réactif. On évite ainsi un abonnement matchMedia par
 * input (il y en a des centaines à l'écran).
 */
export function shouldSuppressAutoFocus(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false
    }
    return (
        window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches
    )
}

/**
 * L'appareil désigne-t-il au DOIGT plutôt qu'au pointeur fin ?
 *
 * ⚠️ Ce n'est **pas** la même question que `useIsMobile`. La largeur dit la
 * place disponible ; `pointer: coarse` dit le geste possible. Une tablette de
 * 1024 px n'est pas « mobile » et n'a pourtant aucun survol — or c'est le
 * survol, pas la largeur, qui conditionne l'ouverture d'une info-bulle. Poser
 * la question sur la largeur laisserait toutes les tablettes muettes.
 */
export function useIsCoarsePointer(): boolean {
    const [coarse, setCoarse] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
        return window.matchMedia('(pointer: coarse)').matches
    })

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return
        const query = window.matchMedia('(pointer: coarse)')
        const sync = () => setCoarse(query.matches)
        query.addEventListener('change', sync)
        sync()
        return () => query.removeEventListener('change', sync)
    }, [])

    return coarse
}

export function useIsMobile(): boolean | undefined {
    const [isMobile, setIsMobile] = useState<boolean | undefined>(detectMobile)

    useEffect(() => {
        const portraitQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
        const landscapeQuery = window.matchMedia('(max-width: 1023px) and (max-height: 499px)')

        const check = () => {
            setIsMobile(portraitQuery.matches || landscapeQuery.matches)
        }

        portraitQuery.addEventListener('change', check)
        landscapeQuery.addEventListener('change', check)
        check()

        return () => {
            portraitQuery.removeEventListener('change', check)
            landscapeQuery.removeEventListener('change', check)
        }
    }, [])

    return isMobile
}
